import type { Question } from "@quizzes/_schema";
import type { ResponseExportRow } from "../api";
import { questionLabel } from "./questions";
import { columnName, type Cell, type Row, type Sheet } from "./xlsx";
import { buildChunkedXlsx, xlsxRowChunk, XlsxSizeLimitError } from "./xlsxStreaming";

export type ResponseExportQuiz = {
  slug: string;
  meta: { title: string };
  questions: Question[];
};

/** Calendar dates shown to the operator; both absent means All time. */
export type ResponseDates = { from?: string; to?: string };
type ExportMetadata = ResponseDates & {
  quiz: ResponseExportQuiz;
  generatedOn: string;
};
type ExportArgs = ExportMetadata & { responses: ResponseExportRow[] };

export const RESPONSE_EXPORT_LIMITS = {
  rows: 1_048_575,
  columns: 16_384,
  bytes: 512 * 1024 * 1024,
} as const;

export class ResponseExportLimitError extends Error {}

const SIZE_LIMIT_MESSAGE = "This Excel file exceeds the 512 MiB download limit. Choose a narrower date range.";

const FIXED_HEADERS = ["Response ID", "Email", "Purchased", "Response date (UTC)", "Quiz slug"];
const PASS_THROUGH = new Set(["info", "summary", "signal-summary", "editorial", "story"]);
const NOTES = [
  ["Population", "One row per saved submission (response ID). Only saved submissions are included; abandoned visits without a saved response are absent."],
  ["Purchased", "Yes means at least one tracked, non-cancelled order was attributed to this response. No tracked purchase is not proof that the person did not buy."],
  ["Attribution", "An eligible order credits the latest response before the order across quizzes in the same session; ties use the highest response ID. Several orders still produce one response row."],
  ["24-hour rule", "Eligibility uses the existing 24-hour rule from the last tracked quiz event when the order was recorded. This is not a new 24-hour window from the response date."],
  ["Date filters", "UTC calendar dates filter response dates, not purchase dates. The first and last displayed dates are included; the API uses the next midnight as its exclusive end."],
  ["Legacy data", "Legacy responses may lack captured email, session links, or order tracking. Blank email means none is saved; missing or null answers remain blank."],
  ["Question labels", "Labels come from the current quiz definition and can change. Headers include question IDs. Historical questions and unrecognized option values retain raw values; original wording may be unavailable."],
  ["Snapshot", "Response IDs are bounded at the start of this download. Email, order, or cancellation updates can still change during paging; purchase status is not a historical snapshot."],
] as const;

function substantive(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "advance" && value !== "";
}

/** Tracks historic keys incrementally so paging never rescans collected rows. */
export function createResponseColumns(quiz: ResponseExportQuiz) {
  const byId = new Map(quiz.questions.map((question) => [question.id, question]));
  const ids = new Set(quiz.questions.filter((question) => !PASS_THROUGH.has(question.type)).map((question) => question.id));
  return {
    add(rows: ResponseExportRow[]) {
      for (const row of rows) {
        for (const id of Object.keys(row.answers).sort()) {
          const value = row.answers[id];
          const question = byId.get(id);
          if (!question || !PASS_THROUGH.has(question.type) || substantive(value)) ids.add(id);
        }
      }
    },
    count() { return FIXED_HEADERS.length + ids.size; },
    ordered() {
      // Append new historical columns without moving cells already serialized.
      return Array.from(ids, (id) => ({ id, question: byId.get(id) }));
    },
  };
}

export function assertResponseWorksheetSize(rows: number, columns: number): void {
  if (rows > RESPONSE_EXPORT_LIMITS.rows) {
    throw new ResponseExportLimitError("This export exceeds Excel's limit of 1,048,575 response rows. Choose a narrower date range.");
  }
  if (columns > RESPONSE_EXPORT_LIMITS.columns) {
    throw new ResponseExportLimitError("This export exceeds Excel's limit of 16,384 columns. Choose a narrower date range.");
  }
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

function answerCell(value: unknown, question?: Question): Cell {
  if (value === null || value === undefined) return "";
  if (question && PASS_THROUGH.has(question.type) && !substantive(value)) return "";
  const labels = new Map(
    question && (question.type === "single" || question.type === "multi")
      ? question.options.map((option) => [option.value, option.label])
      : [],
  );
  if (typeof value === "number") return value;
  if (typeof value === "string") return labels.get(value) ?? value;
  if (question?.type === "multi" && Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === "string")) {
    return value.map((item: string) => labels.get(item) ?? item).join("; ");
  }
  return stableJson(value);
}

function checkedText(value: Cell): Cell {
  const text = value !== null && typeof value === "object" ? value.v : value;
  if (typeof text === "string") {
    if (text.length > 32_767) {
      throw new ResponseExportLimitError("A response exceeds Excel's cell text limit. Narrow the date range to exclude it, or contact support for a raw export.");
    }
    // The shared writer strips XML controls. Response data must either survive
    // unchanged or fail explicitly; also reject invalid XML and lone surrogates.
    for (const character of text) {
      const code = character.codePointAt(0)!;
      const allowedControl = code === 9 || code === 10 || code === 13;
      if ((code < 32 && !allowedControl) || (code >= 0xd800 && code <= 0xdfff) || code === 0xfffe || code === 0xffff) {
        throw new ResponseExportLimitError("A response contains an unsupported Excel character. Narrow the date range to exclude it, or contact support for a raw export.");
      }
    }
  }
  return value;
}

type AnswerColumn = ReturnType<ReturnType<typeof createResponseColumns>["ordered"]>[number];

function headerRow(ordered: AnswerColumn[]): Row {
  const headers = [...FIXED_HEADERS, ...ordered.map(({ id, question }) => question ? `${questionLabel(question)} [${id}]` : id)];
  return { cells: headers.map((header) => checkedText({ v: header, s: "header" })) };
}

function responseRows(rows: ResponseExportRow[], ordered: AnswerColumn[]): Row[] {
  return rows.map((row) => ({
    cells: [
      row.id,
      row.email ?? "",
      row.purchased ? "Yes" : "No tracked purchase",
      new Date(row.created_at).toISOString(),
      row.quiz_slug,
      ...ordered.map(({ id, question }) => Object.hasOwn(row.answers, id) ? answerCell(row.answers[id], question) : ""),
    ].map(checkedText),
  }));
}

function responseSheetMetadata(columns: number, rowCount: number): Omit<Sheet, "rows"> {
  return {
    name: "Responses", columns: [14, 32, 23, 29, 22, ...Array<number>(columns - FIXED_HEADERS.length).fill(42)],
    freeze: "F2", autoFilter: `A1:${columnName(columns)}${rowCount}`,
  };
}

function readmeSheet(args: ExportMetadata): Sheet {
  const range = args.from && args.to ? `${args.from} through ${args.to}, inclusive (UTC)` : "All time";
  return {
    name: "Read me",
    rows: [
      { cells: [{ v: "Quiz response export", s: "title" }, args.quiz.meta.title] },
      { cells: ["Response date range", range] },
      { cells: ["Generated (UTC)", args.generatedOn] },
      ...NOTES.map(([label, note]) => ({ cells: [{ v: label, s: "label" } as Cell, { v: note, s: "wrap" } as Cell], height: 48 })),
    ],
    columns: [24, 115], showGridLines: false,
  };
}

/** Small in-memory formatting helper; production downloads use the paged builder. */
export function buildResponseExportSheets(args: ExportArgs): Sheet[] {
  const columns = createResponseColumns(args.quiz);
  columns.add(args.responses);
  assertResponseWorksheetSize(args.responses.length, columns.count());
  const ordered = columns.ordered();
  return [
    { ...responseSheetMetadata(columns.count(), args.responses.length + 1), rows: [headerRow(ordered), ...responseRows(args.responses, ordered)] },
    readmeSheet(args),
  ];
}

/** Retains serialized Blob pages, never the full population of response objects. */
export function createResponseExport(args: ExportMetadata) {
  const columns = createResponseColumns(args.quiz);
  const chunks: Blob[] = [];
  let loaded = 0;
  let bytes = 0;
  let state: "open" | "failed" | "finished" = "open";
  function requireOpen() {
    if (state !== "open") throw new Error(`The response export is ${state}. Start a new download.`);
  }
  return {
    append(rows: ResponseExportRow[], total?: number): void {
      requireOpen();
      try {
        columns.add(rows);
        assertResponseWorksheetSize(Math.max(total ?? 0, loaded + rows.length), columns.count());
        const chunk = xlsxRowChunk(responseRows(rows, columns.ordered()), loaded + 2);
        if (bytes + chunk.size > RESPONSE_EXPORT_LIMITS.bytes) throw new ResponseExportLimitError(SIZE_LIMIT_MESSAGE);
        chunks.push(chunk);
        bytes += chunk.size;
        loaded += rows.length;
      } catch (cause) {
        state = "failed";
        chunks.length = 0;
        throw cause;
      }
    },
    async finish(signal?: AbortSignal): Promise<Blob> {
      requireOpen();
      state = "finished";
      try {
        assertResponseWorksheetSize(loaded, columns.count());
        const readme = readmeSheet(args);
        return await buildChunkedXlsx([
          {
            ...responseSheetMetadata(columns.count(), loaded + 1),
            rows: new Blob([xlsxRowChunk([headerRow(columns.ordered())], 1), ...chunks]),
            rowCount: loaded + 1, columnCount: columns.count(),
          },
          { ...readme, rows: xlsxRowChunk(readme.rows, 1), rowCount: readme.rows.length, columnCount: 2 },
        ], { ...(signal ? { signal } : {}), maxBytes: RESPONSE_EXPORT_LIMITS.bytes });
      } catch (cause) {
        if (cause instanceof XlsxSizeLimitError) throw new ResponseExportLimitError(SIZE_LIMIT_MESSAGE);
        throw cause;
      } finally {
        chunks.length = 0;
      }
    },
  };
}

export function responseExportFilename(slug: string, dates: ResponseDates): string {
  const range = dates.from && dates.to ? `${dates.from}-to-${dates.to}` : "all-time";
  return `${slug}-responses-${range}.xlsx`;
}
